import AppLayout from "@/components/AppLayout"
import {useMemo, useState, useEffect, CSSProperties} from "react"
import {ResponsivePie} from "@nivo/pie"
import SettingsApplicationsIcon from "@material-ui/icons/SettingsApplications"
import PptInputTailwind from "@/components/PptInputTailwind"
import ArrowForwardIcon from "@material-ui/icons/ArrowForward"
import OpenInNewIcon from "@material-ui/icons/OpenInNew"
import {trpc} from "@/lib/requests/index"
import {AUTH_TOKEN_KEY, PAGINATION_FINISHED, time} from "@/lib/consts"
import {useClientSideAuth} from "@/lib/auth/client"
import {GetServerSidePropsContext} from "next"
import {validateToken} from "@/lib/auth/server"
import {ResponsiveBar} from "@nivo/bar"
import CheckCircleIcon from "@material-ui/icons/CheckCircle"
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank"
import firstStepsData from "@/lib/copy/pathways/firstSteps"
import type { UserSuccessPathway } from "@/lib/server/models/user"
import NextPageIcon from "@material-ui/icons/ArrowForwardIos"
import PreviousPageIcon from "@material-ui/icons/ArrowBackIos"
import InfoIcon from "@material-ui/icons/Info"
import SkeletonRect from "@/components/loading/SkeletonRect"
import {useLocalStorage} from "@/lib/hooks/useLocalStorage"
import {useDebounce} from "@/lib/hooks"
import { usePathwayData } from "@/lib/hooks/usePathwayData"
import UserPathwayProgressLine from "@/components/UserPathwayProgressLine"
import VideoIcon from "@material-ui/icons/MissedVideoCall"
import Link from "next/link"

type Lead30DayPerformance = {
  "lead-page"?: number
  web?: number
  listing?: number
  voice?: number
  sms?: number
  unknown?: number
}

export default function Dashboard(): JSX.Element {
  const _auth = useClientSideAuth("user")
  const trpcContext = trpc.useContext()

  const authType = trpc.useQuery(["user.auth"])
  const dashboardQuery = trpc.useQuery(["leads.dashboardSummary"])
  const checklistQuery = trpc.useQuery(["user.dashboardCheckList"])
  const leadsLast30Days = trpc.useInfiniteQuery(["leads.past30days", {}], {
    getNextPageParam: ({next}) => next === PAGINATION_FINISHED ? undefined : next
  })

  useEffect(() => {
    const leadsQ = leadsLast30Days
    if (!leadsQ.data) {
      return
    } 
    if (leadsQ.hasNextPage) {
      leadsQ.fetchNextPage()
    }
  }, [leadsLast30Days])

  const [editingLeadGoal, setEditingLeadGoal] = useState(false)

  // the lead graph depends on this silly query.
  // To make this much faster (should be investigated) you should 
  // just group the leads registered in the past thirty
  // days on the server rather than paginating multiple times
  // then constructing a graph on the client
  const leads30Days = useMemo(() => {
    const {data} = leadsLast30Days
    if (!data) {
      return []
    }
    const leads: {_id: string, created: Date, sourceType: string}[] = []
    for (const {results} of data.pages) {
      leads.push(...results)
    }
    return leads
  }, [leadsLast30Days])

  const leadsPerDay = useMemo(() => {
    const beginningOfToday = new Date()
    beginningOfToday.setHours(0, 0, 0)
    let startDate = beginningOfToday.getTime()
    const leadsPerDay: ({day: string} & Lead30DayPerformance)[] = []
    for (let i = 29; i > -1; i--) {
      const leadSources: Lead30DayPerformance = {}
      const start = startDate - i * time.milliseconds_per_day
      const end = start + time.milliseconds_per_day
      const groupedSources = leads30Days
        .filter((lead) => {
          const t = new Date(lead.created).getTime()
          return t >= start && t < end
        })
        .reduce((source, next) => {
          const key = next.sourceType as keyof typeof source
          if (source[key] !== undefined) {
            (source[key] as number) += 1
          } else {
            source[key] = 1
          }
          return source
        }, leadSources)
      const d = new Date(start)
      const day = `${d.getMonth() + 1}-${d.getDate()}`
      leadsPerDay.push({...groupedSources, day})
    }
    return leadsPerDay
  }, [leads30Days])

  const updateLeadGoal = trpc.useMutation("user.leadGoal")

  const handleLeadGoalSet = (newGoal: string) => {
    const goal = parseInt(newGoal, 10)
    if (goal >= 0) {
      trpcContext.setQueryData(["user.dashboardCheckList"], (old) => {
        const newData = {...old!}
        newData.settings.monthlyLeadGoal = goal
        return newData
      })
      updateLeadGoal.mutate({goal})
    }
    setEditingLeadGoal(false)
  }

  const mutateUiPrefs = trpc.useMutation("user.uiPrefences")

  const monthlyLeadGoal = (
    checklistQuery.data?.settings?.monthlyLeadGoal || 0
  )
  const isEditingLeadGoal = (
    monthlyLeadGoal < 1  
    || editingLeadGoal
  )

  function onToggleChecklistItem(newKey: string) {
    if (!checklistQuery.data?.uiPrefs) {
      return
    }
    const previous = checklistQuery.data?.uiPrefs || []
    const index = previous.findIndex((p) => p.key === newKey)
    let newPrefs = [...previous]
    const isFound = index > -1
    if (isFound) {
      newPrefs.splice(index, 1)
    } else {
      newPrefs.push({key: newKey} as typeof newPrefs[number])
    }
    trpcContext.setQueryData(
      ["user.dashboardCheckList"], 
      (data) => ({...data!, uiPrefs: newPrefs})
    )
    mutateUiPrefs.mutate(newPrefs)
  }

  const dataPrefs = checklistQuery.data?.uiPrefs || []

  return <AppLayout 
    isAdmin={authType.data === "admin"} 
  >
      <section className="grid grid-cols-1 sm:grid-cols-12 grid-rows-8 w-full px-12">
        {isEditingLeadGoal? <SetLeadGoal
            className="col-span-3 "
            onSetClick={handleLeadGoalSet}
            style={{height: "15rem"}}
          /> : <LeadGoal
            className="col-span-3"
            style={{height: "15rem"}}
            leadCount={dashboardQuery.data?.leadCountLast30 || 0}
            leadGoal={checklistQuery.data?.settings?.monthlyLeadGoal || 0}
            onEditClick={() => setEditingLeadGoal(true)}
        />}

        <LeadsGraph
          className="col-span-9 row-span-4"
          style={{height: "15rem"}}
          data={leadsPerDay}
          showTemplate={!dashboardQuery.data}
        />

        {checklistQuery.isLoading ? <>
          <SkeletonRect className="col-span-12 h-20 row-span-1 mt-4"/>
          <SkeletonRect className="col-span-12 h-48 row-span-1 mt-4"/>
        </> : null}

        {!checklistQuery.isLoading ? (
          <NewUserChecklist
            uiPrefs={dataPrefs}
            userSuccessPathway={checklistQuery.data?.userSuccessPathway || "first-steps"}
            onToggleChecklistItem={onToggleChecklistItem}
          />
        ) : null}
      </section>
  </AppLayout>
}

const DEFAULT_REDIRECT = {redirect: {
  destination: `${process.env.NEXT_PUBLIC_AUTH_BASE_URL}/?redirect=ppt`,
  permanent: false
}} as const

export async function getServerSideProps(
  ctx: GetServerSidePropsContext
) {
  const authToken = ctx.req.cookies[AUTH_TOKEN_KEY]
  if (!authToken) {
    return DEFAULT_REDIRECT
  }
  const payload = validateToken(authToken)
  if (payload.auth === "none") {
    return DEFAULT_REDIRECT
  }
  return {props: {}}
}

export type LeadData = {
  "lead-page"?: number
  web?: number
  listing?: number
  voice?: number
  sms?: number
  unknown?: number
  day: string
}

type LeadsGraphProps = {
  data: LeadData[]
  className?: string
  showTemplate?: boolean
  style?: CSSProperties
}

function LeadsGraph(props: LeadsGraphProps): JSX.Element {
  const { 
    data, 
    className = "", 
    showTemplate = false 
  } = props
  
  const highestLeadCount = data.reduce((highest, day) => {
    const total = (
      (day.listing || 0) 
      + (day.sms || 0) 
      + (day.unknown || 0) 
      + (day.voice || 0) 
      + (day.web || 0)
    )
    return total > highest ? total : highest
  }, 0)

  return (
    <div
      className={
        className +
        " p-4 bg-ppt-gray-200 box-shadow-subtle flex flex-col items-center justify-between border-ppt-blue-800 border-8 relative"
      }
      style={props.style}
    >
      <h3 className="text-ppt-darktext text-xl">30-Day Lead Performance</h3>
      <section
        className={
          className +
          " abs-cover top-8 text-ppt-darktext text-sm" +
          " realtive " +
          (showTemplate ? " opacity-50" : "")
        }
      >
        {" "}
        {showTemplate ? (
          <div
            className={
              "abs-center-30 bg-ppt-blue-900 rounded-lg z-20" +
              " flex flex-col items-center justify-center "
            }
          >
            <span className=" text-ppt-lighttext text-lg w-4/5">
              {
                "Want to see leads like this? Follow the checklist to get your Pipeline set up!"
              }
            </span>
          </div>
        ) : null}
        <ResponsiveBar
          data={data}
          keys={[
            "web", 
            "listing", 
            "sms", 
            "voice", 
            "lead-page", 
            //"unknown"
          ]}
          indexBy="day"
          borderColor={{ from: "color", modifiers: [["darker", 1.9]] }}
          colors={[
            "#0060ad", 
            "#ea1543", 
            "#2fa97f", 
            "#f98d17", 
            "#9F2B68",
            //"#009623",
          ]}
          labelTextColor={"#eee"}
          margin={{ top: 20, right: 120, bottom: 50, left: 50 }}
          enableGridY={false}
          borderRadius={4}
          axisBottom={{
            tickSize: 8,
            tickPadding: 5,
            tickRotation: -90,
            legend: "date",
            legendPosition: "middle",
            legendOffset: 44,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickValues: highestLeadCount,
            tickRotation: 0,
            legend: "leads",
            legendPosition: "middle",
            legendOffset: -36,
          }}
          legends={[
            {
              dataFrom: "keys",
              anchor: "bottom-right",
              direction: "column",
              justify: false,
              translateX: 120,
              translateY: 0,
              itemsSpacing: 2,
              itemWidth: 100,
              itemHeight: 20,
              itemDirection: "left-to-right",
              itemOpacity: 0.85,
              symbolSize: 20,
              effects: [
                {
                  on: "hover",
                  style: {
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}
        ></ResponsiveBar>
      </section>
    </div>
  )
}

type LeadGoalProps = {
  className?: string,
  leadCount?: number,
  leadGoal?: number,
  onEditClick? : () => unknown
  style?: CSSProperties
}

function LeadGoal(props: LeadGoalProps): JSX.Element {
  const {
    className = "",
    leadCount = 15,
    leadGoal = 55,
    onEditClick,
  } = props
  const ratio = leadCount / leadGoal
  const color = ratio > 0.9 ? "#2fa97f" : ratio > 0.75 ? "#f98d17" : "#ea1543"

  return <section
    className={
      className +
      " bg-ppt-gray-200 flex items-center justify-center flex-wrap" +
      " box-shadow-subtle border-8 border-ppt-blue-800" +
      " relative"
    }
    style={props.style}
  >
    <button
      onClick={onEditClick}
      className="abs-top-right text-ppt-blue hover:text-ppt-red opacity-75 cursor-pointer z-10"
    >
      <SettingsApplicationsIcon/>
    </button>
    
    <div className="flex flex-col items-center justify-center text-ppt-darktext px-2 text-center">
      <span className="font-display text-sm md:text-base text-wrap">
        {"30-Day Lead Goal"}
      </span>
      <span className="inline-flex items-center text-3xl sm:text-4xl md:text-5xl font-display text-ppt-blue">
        <span style={{color}}>{leadCount}</span>
        {"/"}
        <span>{leadGoal}</span>
      </span>
    </div>

    <div className="relative w-32 h-32">
      <div className="abs-cover p-2">
        <ResponsivePie
          data={[
            {
              id: "leads",
              label: "leads",
              value: ratio,
            },
            {
              id: "goal",
              label: "goal",
              value: 1 - ratio,
              color: "#00000000",
            },
          ]}
          colors={[color, "#b1b1b1"]}
          fit={false}
          startAngle={0}
          endAngle={360}
          innerRadius={0.8}
          enableRadialLabels={false}
          enableSliceLabels={false}
          sliceLabel={""}
          cornerRadius={4}
          isInteractive={false}
        />
      </div>
    </div>
  </section>
}

type SetLeadGoalProps = {
  className?: string
  onSetClick: (newGoal: string) => unknown
  style?: CSSProperties
}

function SetLeadGoal(props: SetLeadGoalProps): JSX.Element {
  const {
    className = "", onSetClick
  } = props
  
  const [goal, setGoal] = useState(0)

  return (
    <section
      className={
        className +
        " flex flex-col items-center p-4 bg-ppt-blue-800" +
        " box-shadow-subtle text-ppt-lighttext relative"
      }
      style={props.style}
    >
      <h3 className="text-center text-base">
        {"What's your monthly lead goal?"}
      </h3>
      <a
        className={
          "w-full bg-ppt-blue-900 text-ppt-darkgreen text-sm" +
          " flex p-2 items-center justify-center mt-2"
        }
        target="_blank"
        rel="noopener noreferrer"
        href="https://docs.google.com/spreadsheets/d/1XnuUDACgCdE0uZFSx0bl-aWdNPQSKaSkPcbgNoYF9EE/edit#gid=0"
      >
        <OpenInNewIcon className="mr-2"></OpenInNewIcon>
        {"Use our calculator..."}
      </a>

      <PptInputTailwind
        className="mt-2 w-full lg:w-1/2"
        labelText={"Lead Goal"}
        type="number"
        inputClassName="w-full"
        value={String(goal)}
        defaultValue={""}
        onChange={(e) => setGoal(parseInt(e.target.value, 10))}
        placeholder="lead goal"
      />
      <button
        className={
          " abs-lower-right mr-4 mb-4" +
          " text-ppt-success font-display" +
          " text-lg hover:text-ppt-darkgreen"
        }
        onClick={() => onSetClick(String(goal))}
      >
        <span>{"set"}</span>
        <ArrowForwardIcon></ArrowForwardIcon>
      </button>
    </section>
  )
}

type NewUserChecklistProps = {
  className?: string
  uiPrefs: { key: string }[]
  userSuccessPathway: UserSuccessPathway | (string & {})
  onToggleChecklistItem: (newKey: string) => unknown
}

function NewUserChecklist(
  props: NewUserChecklistProps
): JSX.Element {
  const pathwayData = usePathwayData(props.userSuccessPathway)

  const [pageIndex, setPageIndex] = useLocalStorage(
    "user-pathway-page", 0
  )

  function incrementPageIndex(increment: number) {
    if (!pathwayData) {
      return
    }
    const next = pageIndex + increment
    const upperbound = pathwayData.length + 1
    const isOutOfBounds = (
      next < 0 || next >= upperbound
    )
    if (isOutOfBounds) {
      return
    }
    setPageIndex(next)
  }

  const isFirstPage = pageIndex === 0 || !pathwayData
  const currentPage = isFirstPage
    ? firstStepsData
    : pathwayData[pageIndex - 1] || firstStepsData

  const pageTitle = currentPage.title

  const pageTasksCompleted = useMemo(() => {
    let sum = 0
    for (const pref of props.uiPrefs) {
      const includesPref = currentPage.items.some(
        (i) => i.id === pref.key
      )
      if (includesPref) {
        sum += 1
      }
    }
    return sum
  }, [currentPage, props.uiPrefs])

  const canNavigateToOtherPages = (
    props.userSuccessPathway !== "first-steps"
  )

  const pageLimit = (pathwayData?.length || 0) + 1
  const canJumpToNextPage = pageIndex < pageLimit - 1 
  const canJumpToPrevPage = pageIndex > 0

  const orderedItems = useMemo(() => {
      const {uiPrefs} = props
      const todo = []
      const done = []
      for (const item of currentPage.items) {
        const isComplete = uiPrefs.some((p) => p.key === item.id)
        if (isComplete) {
          done.push(item)
        } else {
          todo.push(item)
        }
      }
      todo.push(...done)
      return todo
  }, [currentPage])

  const [tasksIncremented, setTasksIncremented] = useState(false)
  const taskDebounce = useDebounce(3_600)

  function onToggleItem(id: string) {
    const isPresent = props.uiPrefs.some((p) => p.key === id)
    props.onToggleChecklistItem(id)
    if (isPresent) {
      return
    }
    setTasksIncremented(true)
    taskDebounce(() => {
      setTasksIncremented(false)
    })
  }

  return <div className={props.className + " col-span-12"}>
    <section className="col-span-12 row-span-1 bg-ppt-blue-800 box-shadow-subtle mt-2 p-4 rounded">
      <h1>{"Success Progress"}</h1>

      <UserPathwayProgressLine
        uiPrefs={props.uiPrefs}
        animate={tasksIncremented}
        userSuccessPathway={props.userSuccessPathway}
      />
    </section>

    <section className="col-span-12 row-span-1 bg-ppt-blue-800 box-shadow-subtle mt-2 px-4 py-2 rounded">
      <div className="relative w-full">
        <div className="text-left border-gray-500 border-b-2 pb-2">
          <div className="text-base font-display text-gray-400">
            {pageTitle}
          </div>

          <div className="text-xs">
            {`${pageTasksCompleted} / ${currentPage.items.length} complete`}
          </div>
        </div>
      </div>

      <div 
        className="w-full overflow-y-scroll h-40 pt-1 mt-1"
      >
        {orderedItems.map((item) => <ChecklistItem
          {...item}
          key={item.id}
          done={props.uiPrefs.some((p) => p.key === item.id)}
          className="ml-3"
          onToggleItem={() => onToggleItem(item.id)}
        />)}
      </div>

      <div className="w-full flex text-left pt-1">
        <div className="w-1/2 text-left"/>

        <div className="w-1/2 text-right">
          {canNavigateToOtherPages ? <span 
            className="ml-2"
          >
            <span className="mr-3 text-xs text-gray-600">
              {"page "}
              <span className="text-purple-500">
                {pageIndex + 1}
              </span>
              {` of ${pageLimit}`}
            </span>

            <button 
              className={`hover:text-gray-800 ${!canJumpToPrevPage ? "text-gray-800" : ""}`} 
              title="previous page"
              disabled={!canJumpToPrevPage}
              onClick={() => incrementPageIndex(-1)}
            >
              <PreviousPageIcon style={{fontSize: "1.2rem"}}/>
            </button>

            <button 
              className={`hover:text-gray-800 ${!canJumpToNextPage ? "text-gray-800" : ""}`} 
              title="next page"
              disabled={!canJumpToNextPage}
              onClick={() => incrementPageIndex(1)}
            >
              <NextPageIcon style={{fontSize: "1.2rem"}}/>
            </button>
          </span> : null}
        </div>
      </div>
    </section>
  </div>
}

type ChecklistItemProps = {
  done?: boolean
  title?: string
  className?: string
  info?: string
  videoUrl?: string
  onToggleItem: () => unknown
}

function ChecklistItem(props: ChecklistItemProps): JSX.Element {
  const {
    done = false,
    title: text = "checklist",
  } = props

  const isLinkOrPlaybook = (
    props.info?.startsWith("http")
    || props.info?.startsWith("/playbooks/view")
  )

  const infoRedirect = useMemo(() => {
    const {info} = props
    if (!info) {
      return ""
    }
    if (info.startsWith("pb:")) {
      const playbookhash = info.replace("pb:", "")
      return `/playbooks?highlight=${playbookhash}`
    }
    return info
  }, [props.info])

  return <div className={props.className + " my-1 text-sm"}>
    <button 
      className={`hover:text-ppt-success ${done ? " text-ppt-success line-through" : ""}`} 
      onClick={props.onToggleItem}
    >
      {!done ?
        <CheckBoxOutlineBlankIcon style={{marginRight: "0.15rem"}}/>
       : <CheckCircleIcon style={{marginRight: "0.15rem"}}/>
      }
      <span>
        {text}
      </span>
    </button>

    {infoRedirect ? <Link href={infoRedirect}>
      <a  
        target={isLinkOrPlaybook ? "_blank" : ""}
        className="ml-2 inline-block mb-2 hover:text-blue-500"
      >
        <InfoIcon style={{fontSize: "1.1rem"}} />
      </a>
    </Link> : null}

    {props.videoUrl ? <a 
      href={props.videoUrl} 
      target={"_blank"}
      title="View Help Video"
      className="ml-2 inline-block mb-2 hover:text-purple-500"
    >
      <VideoIcon style={{fontSize: "1.1rem"}} />
    </a> : null}
  </div>
}